import { useEffect, useState } from 'react'
import { useSession } from './useSession'
import { usePath } from './routing'
import { consumeReturnPath } from './returnPath'
import { Guide } from '../guide/Guide'
import { Shared } from '../shared/Shared'
import { SharedSetup } from '../shared/SharedSetup'
import { Workspace } from '../room/Workspace'
import { SetPassword, consumePasswordToken } from '../auth/SetPassword'
import { SignIn } from '../auth/SignIn'
import type { Me } from '../api/types'
import type { ExtensionPage, WebExtension } from '../extensions/types'
import installedExtension from '@stagepost/extension'

const extension: WebExtension = installedExtension

function matches(page: ExtensionPage, path: string): boolean {
  return path === page.path || (page.prefix === true && path.startsWith(`${page.path}/`))
}

/**
 * The gate.
 *
 * Signed in gets the app; signed out gets the extension's signed-out home, or
 * the sign-in screen when there is none. That is the whole routing decision at
 * this level — the entry model is a web app, and a logged out visitor is a
 * reader, not an error.
 *
 * Two pages answer to neither, and both sit above the loading branch.
 *
 * An emailed password link goes first of all — see the comment on its branch;
 * the token is already out of the URL by the time anything renders, so nothing
 * may return ahead of it.
 *
 * The guide comes next. It is public, and it carries the answer to "stagepost
 * is not answering", so it has to paint whether or not the server can say who
 * you are. It gets the session status only to decide between offering a
 * sign-up and offering the way back into the app.
 *
 * An extension's own pages come after the guide, public for the same reason.
 *
 * A shared publication is public in the stronger sense. The guide is a page
 * anybody may read; `/shared/<token>` is a page whoever holds the token may
 * read, and the token is the entire authorisation. It never asks who you are,
 * so it must not sit behind a branch that waits to find out.
 */
export function App() {
  const { state, setMe } = useSession()
  // Read on the first render, not in an effect: the link from an email is this
  // page's first paint, and an effect would flash the signed-out home first.
  const [passwordToken, setPasswordToken] = useState(consumePasswordToken)
  // Set only when `/v1/oauth/authorize` sent this browser here to sign in
  // first. Read once, same reason as the password token above.
  const [returnPath] = useState(consumeReturnPath)
  const path = usePath()
  const passwordLink = passwordToken !== null

  // The open app counts nothing itself; whatever an extension does with a
  // navigation is its own business.
  useEffect(() => {
    extension.onNavigate?.({ path, sessionStatus: state.status, passwordLink })
  }, [path, state.status, passwordLink])

  // A full-page navigation, not the in-app `navigate`: the destination is
  // `/v1/oauth/authorize`, outside this SPA's own routes, and the consent
  // screen it shows next depends on the session cookie signing in just set,
  // which only an actual request carries.
  function handleSignedIn(me: Me) {
    if (returnPath) {
      window.location.href = returnPath
      return
    }
    setMe(me)
  }

  // Above every other branch, including /guide and the signed-in one.
  //
  // Two reasons, and the second is the load-bearing one. Somebody already
  // logged in on this browser who follows a reset link wants to set a
  // password, not to be dropped into the app as though nothing happened. And
  // `consumePasswordToken` runs in the state initializer above — it strips the
  // token from the URL on first render whatever renders next — so any branch
  // returning ahead of this one throws a live credential away in silence.
  if (passwordToken) {
    return (
      <SetPassword
        token={passwordToken}
        onSignedIn={(me) => {
          setMe(me)
          setPasswordToken(null)
        }}
      />
    )
  }

  if (path === '/guide' || path.startsWith('/guide/')) {
    return <Guide session={state.status} signUpAction={extension.signUpAction} />
  }

  // Above the session gate, like the guide: an extension's page decides for
  // itself what the session means to it, and none of them may wait to find out.
  const page = extension.pages?.find((candidate) => matches(candidate, path))
  if (page) {
    return <>{page.render(path)}</>
  }

  // Above the loading gate for the same reason the guide is: holding the token
  // is the whole authorisation, so this page never needs to know who you are
  // and must not wait to find out.
  if (path.startsWith('/shared/')) {
    return <Shared token={decodeURIComponent(path.slice('/shared/'.length))} />
  }

  // A record link, above the session gate for the reason `/shared/` is: holding
  // the token is the whole authorisation. Its own top-level path rather than
  // `/shared/setups/`, because the branch above matches `startsWith('/shared/')`
  // and would read `setups/<token>` as a publication token.
  if (path.startsWith('/setups/')) {
    return <SharedSetup token={decodeURIComponent(path.slice('/setups/'.length))} />
  }

  if (state.status === 'loading') {
    return (
      <div style={{ padding: 40, color: 'var(--text-dim)' }} role="status">
        <span style={{ animation: 'sp-pulse 1.6s ease-in-out infinite' }}>
          stagepost
        </span>
      </div>
    )
  }

  if (state.status === 'unreachable') {
    return (
      <div style={{ padding: 40, maxWidth: 460 }} role="alert">
        <p style={{ fontFamily: 'var(--font-human)', fontSize: 17 }}>
          stagepost is not answering.
        </p>
        <p style={{ color: 'var(--text-dim)', fontSize: 13, lineHeight: 1.6 }}>
          This is the server, not your password. Your agents keep working; they
          just cannot hear each other until it is back.
        </p>
        <p style={{ color: 'var(--text-faint)', font: '400 11px var(--font-machine)' }}>
          {state.detail}
        </p>
      </div>
    )
  }

  if (state.status === 'signed-out') {
    const SignedOutHome = extension.SignedOutHome ?? SignIn
    return <SignedOutHome onSignedIn={handleSignedIn} />
  }

  // `setMe` is threaded down so a name changed in settings reaches the rail
  // without a reload — otherwise the field saves and the app keeps showing the
  // old name, which reads as the save having failed.
  return <Workspace me={state.me} onMeChanged={setMe} />
}
